/*
  fp-digital-consent.js
  Herroepingsrecht-vinkje bij digitale varianten (EU-richtlijn 2011/83
  art. 16(m)): het herroepingsrecht vervalt alleen als de klant vóór levering
  uitdrukkelijk instemt met directe levering én erkent dat hij daarmee zijn
  herroepingsrecht verliest. Dit script toont/verbergt het blok uit
  fp-digital-consent.liquid op variantwissel (MutationObserver-patroon uit
  fp-addon.js) en zet/verwijdert een hidden `properties[_withdrawal_waiver]`
  input in het product-form zolang het vinkje aan staat.

  Werkt zowel mét als zonder fp-ai-generator.js op de pagina: de submit-gate
  hier is het enige dat een gewoon product-form tegenhoudt; fp-ai-generator.js
  heeft zijn eigen gate (isDigitalConsentBlocked) voor het modal-form en
  luistert op het 'fp-digital-consent:change'-event dat dit bestand stuurt
  zodat de dynamic checkout-knoppen (Buy now/Shop Pay) meegaten.
*/
(function () {
  if (window.__fpDigitalConsentInit) return;
  window.__fpDigitalConsentInit = true;

  const PROPERTY_NAME = 'properties[_withdrawal_waiver]';
  const PROPERTY_VALUE = 'yes';

  function setHiddenInput(form, name, value, on) {
    let input = form.querySelector(`input[name="${CSS.escape(name)}"]`);
    if (on) {
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.dataset.fpDigitalConsent = '1';
        form.appendChild(input);
      }
      input.value = value;
    } else if (input) {
      input.remove();
    }
  }

  // Twee properties uit één vinkje: de verborgen `_withdrawal_waiver` is de
  // machine-sleutel voor de orderflow; de zichtbare (vertaald label + waarde)
  // komt in cart, checkout en de orderbevestigingsmail terecht en levert zo de
  // bevestiging op duurzame drager uit art. 8(7) Richtlijn 2011/83.
  function ensureWaiverInput(form, checked, root) {
    setHiddenInput(form, PROPERTY_NAME, PROPERTY_VALUE, checked);
    const label = root && root.dataset.propertyLabel;
    const value = root && root.dataset.propertyValue;
    if (label && value) setHiddenInput(form, `properties[${label}]`, value, checked);
  }

  function notifyChange(root) {
    document.dispatchEvent(new CustomEvent('fp-digital-consent:change', { detail: { root } }));
  }

  function initBlock(root) {
    if (root.__fpDigitalConsentBound) return;
    root.__fpDigitalConsentBound = true;

    const checkbox = root.querySelector('.fp-pdp-consent__cb');
    const errorEl = root.querySelector('[data-fp-digital-consent-error]');
    const digitalIds = (root.dataset.digitalVariants || '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
    if (!checkbox || !digitalIds.length) return;

    function getForm() {
      // Na relocateBuyBlocks (fp-ai-generator.js) staat dit blok en het form
      // allebei in dezelfde modal-buyPanel; zonder generator staat het form
      // gewoon op de pagina. Zoeken via het bewaarde form-id, niet via
      // closest('form'): relocatie verplaatst dit blok los van het form-DOM.
      return document.getElementById(root.dataset.productFormId);
    }

    function getVariantInput() {
      const form = getForm();
      return form?.querySelector('[name="id"]') ||
        document.querySelector('product-form form [name="id"]');
    }

    function currentVariantIsDigital() {
      const variantInput = getVariantInput();
      if (!variantInput) return false;
      return digitalIds.includes(String(variantInput.value));
    }

    // Buy with Shop / PayPal / Google Pay lopen niet via het submit-event van
    // het form: de web component (gesloten shadow DOM) serialiseert het form
    // zelf en navigeert weg. Twee lagen: de klasse zet pointer-events:none op
    // de wrapper (erft door de shadow-grens), en de capture-listener vangt
    // wat er toch doorheen komt, zoals een Enter op de gefocuste knop.
    function gateDynamicCheckout() {
      const form = getForm();
      const wrapper = form && form.querySelector('.shopify-payment-button');
      if (!wrapper) return;
      const blocked = currentVariantIsDigital() && !checkbox.checked;
      wrapper.classList.toggle('fp-digital-consent-blocked', blocked);
      wrapper.setAttribute('aria-disabled', String(blocked));
      if (!wrapper.__fpDigitalConsentGate) {
        wrapper.__fpDigitalConsentGate = true;
        wrapper.addEventListener('click', (event) => {
          if (!currentVariantIsDigital() || checkbox.checked) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          if (errorEl) errorEl.hidden = false;
          checkbox.focus();
        }, true);
      }
    }

    function applyVariant() {
      const digital = currentVariantIsDigital();
      gateDynamicCheckout();
      if (digital === !root.hidden) return;
      root.hidden = !digital;
      if (!digital) {
        // Wissel naar een fysieke variant: vinkje en waiver-property altijd
        // opruimen, anders blijft een oude instemming onterecht meelopen.
        checkbox.checked = false;
        if (errorEl) errorEl.hidden = true;
        const form = getForm();
        if (form) ensureWaiverInput(form, false, root);
        notifyChange(root);
      }
    }

    checkbox.addEventListener('change', () => {
      const form = getForm();
      if (form) ensureWaiverInput(form, checkbox.checked, root);
      if (checkbox.checked && errorEl) errorEl.hidden = true;
      gateDynamicCheckout();
      notifyChange(root);
    });

    // Submit-gate voor het gewone product-form (zonder fp-ai-generator):
    // capture-fase, zodat dit vóór Dawn's eigen submit-handler draait.
    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement) || form.getAttribute('id') !== root.dataset.productFormId) return;
        if (!currentVariantIsDigital()) return;
        if (checkbox.checked) return;
        event.preventDefault();
        event.stopPropagation();
        if (errorEl) errorEl.hidden = false;
        checkbox.focus();
      },
      true
    );

    applyVariant();
    const variantInput = getVariantInput();
    if (variantInput) {
      new MutationObserver(() => applyVariant()).observe(variantInput, { attributes: true });
    }
    document.addEventListener('change', () => requestAnimationFrame(() => applyVariant()));
  }

  function initAll() {
    document.querySelectorAll('[data-fp-digital-consent]').forEach(initBlock);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  // fp-ai-generator.js relocateert dit blok pas bij het openen van de modal
  // (na connectedCallback); nieuwe blokken die later in de DOM verschijnen
  // (section-herrendering, quick-add) alsnog oppikken.
  new MutationObserver(() => initAll()).observe(document.body, { childList: true, subtree: true });
})();
